<template>
  <div>
    <!-- ============================================================
         Dialog Quản lý gia hạn Gói Học Phí (tutition_v3)
         Sử dụng: <package-renewal-dialog ref="renewalDialog" />
         Mở:     this.$refs.renewalDialog.open(packageRow)
         ============================================================ -->
    <dialog-base
      :visible.sync="visible"
      :title="`Quản lý tự động gia hạn: ${packageName}`"
      size="xl"
      top="4vh"
      :use-panel="false"
      :show-footer="true"
      @cancel="handleClose"
    >
    <div class="prd-content">
      <!-- Panel Header thông tin gói -->
      <div class="sk-panel mb-3">
        <div class="sk-panel-head">
          <div class="sk-panel-icon"><i class="el-icon-refresh" /></div>
          <div class="prd-panel-text">
            <p class="sk-panel-title">Gói học phí: {{ packageName }}</p>
            <p class="sk-panel-subtitle">
              Quản lý danh sách học sinh đăng ký tự động gia hạn, theo dõi thống kê và đợt thu
            </p>
          </div>
          <div class="prd-meta-badges">
            <el-button
              type="primary"
              plain
              size="mini"
              icon="el-icon-question"
              class="mr-2"
              @click="openGuideDialog"
            >
              Hướng dẫn
            </el-button>
            <el-tag v-if="packageAutoRenew" type="success" size="small" effect="dark">
              <i class="el-icon-check" /> Tự động gia hạn: Đang bật
            </el-tag>
            <el-tag v-else type="info" size="small" effect="plain">
              <i class="el-icon-close" /> Tự động gia hạn: Đang tắt
            </el-tag>
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <el-tabs v-model="activeTab" type="card" class="sk-tabs prd-tabs" @tab-click="handleTabClick">

        <!-- ══════════════════════════════════════════════════════
             TAB 1: THỐNG KÊ
             ══════════════════════════════════════════════════════ -->
        <el-tab-pane name="STATS">
          <template slot="label">
            <i class="el-icon-data-analysis" /> Thống kê gia hạn
          </template>

          <div v-loading="loadingStats">
            <!-- Thống kê tổng quan -->
            <div class="prd-stats-grid">
              <div class="prd-stat-card prd-stat-card--active">
                <div class="prd-stat-icon"><i class="el-icon-check" /></div>
                <div class="prd-stat-body">
                  <span class="prd-stat-value">{{ stats.activeRenewalCount ?? '—' }}</span>
                  <span class="prd-stat-label">Đang tự động gia hạn</span>
                </div>
              </div>
              <div class="prd-stat-card prd-stat-card--paused">
                <div class="prd-stat-icon"><i class="el-icon-video-pause" /></div>
                <div class="prd-stat-body">
                  <span class="prd-stat-value">{{ stats.pausedRenewalCount ?? '—' }}</span>
                  <span class="prd-stat-label">Tạm dừng gia hạn</span>
                </div>
              </div>
              <div class="prd-stat-card prd-stat-card--cancelled">
                <div class="prd-stat-icon"><i class="el-icon-circle-close" /></div>
                <div class="prd-stat-body">
                  <span class="prd-stat-value">{{ stats.cancelledRenewalCount ?? '—' }}</span>
                  <span class="prd-stat-label">Đã hủy gia hạn</span>
                </div>
              </div>
              <div class="prd-stat-card prd-stat-card--due">
                <div class="prd-stat-icon"><i class="el-icon-alarm-clock" /></div>
                <div class="prd-stat-body">
                  <span class="prd-stat-value">{{ stats.dueTodayCount ?? '—' }}</span>
                  <span class="prd-stat-label">Đến hạn hôm nay</span>
                </div>
              </div>
              <div class="prd-stat-card prd-stat-card--cycle">
                <div class="prd-stat-icon"><i class="el-icon-refresh" /></div>
                <div class="prd-stat-body">
                  <span class="prd-stat-value">{{ stats.cycleCount ?? '—' }}</span>
                  <span class="prd-stat-label">Tổng đợt gia hạn</span>
                </div>
              </div>
              <div class="prd-stat-card prd-stat-card--invoiced">
                <div class="prd-stat-icon"><i class="el-icon-document-checked" /></div>
                <div class="prd-stat-body">
                  <span class="prd-stat-value">{{ stats.invoicedStudentCycleCount ?? '—' }}</span>
                  <span class="prd-stat-label">Đã tạo phiếu thu</span>
                </div>
              </div>
              <div class="prd-stat-card prd-stat-card--pending">
                <div class="prd-stat-icon"><i class="el-icon-time" /></div>
                <div class="prd-stat-body">
                  <span class="prd-stat-value">{{ stats.pendingStudentCycleCount ?? '—' }}</span>
                  <span class="prd-stat-label">Chờ tạo phiếu thu</span>
                </div>
              </div>
            </div>

            <!-- Cycle mới nhất -->
            <div v-if="stats.latestCycleNo != null" class="prd-latest-cycle">
              <div class="prd-latest-cycle-head">
                <i class="el-icon-date" />
                <span class="prd-latest-cycle-title">Đợt gia hạn gần nhất</span>
                <el-tag type="primary" size="small" class="ml-2">Đợt #{{ stats.latestCycleNo }}</el-tag>
              </div>
              <div class="prd-latest-cycle-dates">
                <span class="prd-date-label">Thời gian áp dụng:</span>
                <span class="prd-date-value">{{ formatDate(stats.latestCycleStartDate) }}</span>
                <i class="el-icon-right prd-date-arrow" />
                <span class="prd-date-value">{{ formatDate(stats.latestCycleEndDate) }}</span>
              </div>
            </div>
            <div v-else-if="!loadingStats" class="prd-empty-cycle">
              <i class="el-icon-info" /> Chưa có đợt gia hạn nào được xử lý
            </div>

            <!-- Nút reload -->
            <div class="prd-stats-action">
              <el-button size="small" icon="el-icon-refresh" @click="fetchStats">
                Tải lại thống kê
              </el-button>
            </div>
          </div>
        </el-tab-pane>

        <!-- ══════════════════════════════════════════════════════
             TAB 2: DANH SÁCH ĐĂNG KÝ
             ══════════════════════════════════════════════════════ -->
        <el-tab-pane name="LIST">
          <template slot="label">
            <i class="el-icon-list" /> Danh sách đăng ký
          </template>

          <div class="prd-note-box mb-3">
            <i class="el-icon-info" />
            Danh sách dưới đây hiển thị các học sinh đang đăng ký gia hạn hoặc đang tạm dừng. Học sinh đã hủy sẽ xuất hiện lại tại tab <strong>Đăng ký hàng loạt</strong> để đăng ký lại khi cần.
          </div>

          <div class="prd-table-wrapper" v-loading="loadingList">
            <el-table
              :data="renewalList"
              border
              class="sk-table"
              :max-height="400"
              empty-text="Không có học sinh nào đang đăng ký tự động gia hạn"
              @selection-change="handleRenewalSelectionChange"
            >
              <el-table-column type="selection" align="center" width="50" />
              <el-table-column type="index" label="STT" width="55" align="center" />

              <el-table-column prop="studentName" label="Học sinh" min-width="160">
                <template slot-scope="{ row }">
                  <span class="prd-student-name">{{ row.studentName || '—' }}</span>
                </template>
              </el-table-column>

              <el-table-column label="Ngày tham gia" width="120" align="center">
                <template slot-scope="{ row }">
                  {{ formatDate(row.enrollDate) }}
                </template>
              </el-table-column>

              <el-table-column label="Chu kỳ hiện tại" min-width="190" align="center">
                <template slot-scope="{ row }">
                  <span v-if="row.currentCycleStartDate || row.currentCycleEndDate">
                    {{ formatDate(row.currentCycleStartDate) }}
                    <i class="el-icon-right mx-1 text-gray-400" />
                    {{ formatDate(row.currentCycleEndDate) }}
                  </span>
                  <span v-else class="prd-text-muted">Chưa có cycle</span>
                </template>
              </el-table-column>

              <el-table-column label="Ngày hết hạn" width="130" align="center">
                <template slot-scope="{ row }">
                  <span :class="{ 'prd-text-danger': isExpiringSoon(row.currentCycleEndDate) }">
                    {{ formatDate(row.currentCycleEndDate) }}
                  </span>
                </template>
              </el-table-column>

              <el-table-column label="Gia hạn tiếp theo" width="140" align="center">
                <template slot-scope="{ row }">
                  <span v-if="row.nextRenewalDate" class="prd-text-primary">
                    {{ formatDate(row.nextRenewalDate) }}
                  </span>
                  <span v-else class="prd-text-muted">—</span>
                </template>
              </el-table-column>

              <el-table-column label="Trạng thái" width="130" align="center">
                <template slot-scope="{ row }">
                  <el-tag :type="getRenewalStatusType(row.status)" size="small" effect="light">
                    {{ getRenewalStatusLabel(row.status) }}
                  </el-tag>
                </template>
              </el-table-column>

              <el-table-column label="Tác vụ" width="160" align="center" fixed="right">
                <template slot-scope="{ row }">
                  <!-- PAUSED -->
                  <el-tooltip content="Tạm dừng" placement="top">
                    <el-button
                      size="mini"
                      type="warning"
                      icon="el-icon-video-pause"
                      circle
                      plain
                      :disabled="row.status === 'PAUSED' || row.status === 'CANCELLED' || row.status === 'EXPIRED' || !!statusLoadingMap[row.id]"
                      :loading="statusLoadingMap[row.id] === 'PAUSED'"
                      @click="handleUpdateStatus(row, 'PAUSED')"
                    />
                  </el-tooltip>
                  <!-- ACTIVE -->
                  <el-tooltip content="Kích hoạt lại" placement="top">
                    <el-button
                      size="mini"
                      type="success"
                      icon="el-icon-video-play"
                      circle
                      plain
                      class="ml-1"
                      :disabled="row.status === 'ACTIVE' || row.status === 'CANCELLED' || row.status === 'EXPIRED' || !!statusLoadingMap[row.id]"
                      :loading="statusLoadingMap[row.id] === 'ACTIVE'"
                      @click="handleUpdateStatus(row, 'ACTIVE')"
                    />
                  </el-tooltip>
                  <!-- CANCELLED -->
                  <el-tooltip content="Hủy đăng ký" placement="top">
                    <el-button
                      size="mini"
                      type="danger"
                      icon="el-icon-circle-close"
                      circle
                      plain
                      class="ml-1"
                      :disabled="row.status === 'CANCELLED' || row.status === 'EXPIRED' || !!statusLoadingMap[row.id]"
                      :loading="statusLoadingMap[row.id] === 'CANCELLED'"
                      @click="handleUpdateStatus(row, 'CANCELLED')"
                    />
                  </el-tooltip>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <div class="prd-list-action mt-3">
            <el-button
              v-if="selectedRenewals.length"
              size="small"
              type="danger"
              icon="el-icon-circle-close"
              :loading="bulkCancelLoading"
              @click="cancelSelectedRenewals"
            >
              Hủy đăng ký đã chọn ({{ selectedRenewals.length }})
            </el-button>
            <el-button
              v-if="selectedRenewals.length"
              size="small"
              type="warning"
              icon="el-icon-video-pause"
              :loading="bulkStatusLoading"
              :disabled="selectedCountByStatus('ACTIVE') === 0"
              @click="updateSelectedRenewals('PAUSED')"
            >
              Tạm dừng đã chọn ({{ selectedCountByStatus('ACTIVE') }})
            </el-button>
            <el-button
              v-if="selectedRenewals.length"
              size="small"
              type="success"
              icon="el-icon-video-play"
              :loading="bulkStatusLoading"
              :disabled="selectedCountByStatus('PAUSED') === 0"
              @click="updateSelectedRenewals('ACTIVE')"
            >
              Kích hoạt lại đã chọn ({{ selectedCountByStatus('PAUSED') }})
            </el-button>
            <el-button size="small" icon="el-icon-refresh" @click="fetchRenewalList">
              Tải lại danh sách
            </el-button>
          </div>
        </el-tab-pane>

        <!-- ══════════════════════════════════════════════════════
             TAB 3: ĐĂNG KÝ HÀNG LOẠT
             ══════════════════════════════════════════════════════ -->
        <el-tab-pane name="REGISTER">
          <template slot="label">
            <i class="el-icon-circle-plus-outline" /> Đăng ký hàng loạt
          </template>

          <!-- Ghi chú: Chu kỳ tự tính từ ngày học sinh tham gia -->
          <div class="prd-note-box mb-3">
            <i class="el-icon-info" />
            Ngày bắt đầu chu kỳ gia hạn được tính tự động từ <strong>ngày học sinh tham gia gói</strong>. Hệ thống tự tạo chu kỳ mới ngay sau khi chu kỳ hiện tại kết thúc.
          </div>

          <!-- Bộ lọc -->
          <div class="prd-filter-card mb-3">
            <div class="prd-filter-grid">
              <el-select
                v-model="searchRegister.idGrade"
                placeholder="Chọn khối"
                clearable
                class="prd-filter-control--grade"
                @change="handleRegisterGradeChange"
                @clear="handleRegisterGradeChange"
              >
                <el-option
                  v-for="item in gradeList"
                  :key="item.id"
                  :value="item.id"
                  :label="item.gradeName"
                />
              </el-select>

              <el-select
                v-model="searchRegister.idClass"
                placeholder="Chọn lớp"
                clearable
                class="prd-filter-control--class"
                @change="fetchMembers"
                @clear="fetchMembers"
              >
                <el-option
                  v-for="item in classListRegister"
                  :key="item.id"
                  :value="item.id"
                  :label="item.className"
                />
              </el-select>

              <el-input
                v-model="searchRegister.fullName"
                placeholder="Tìm theo tên học sinh..."
                clearable
                class="prd-search-input"
                @keyup.enter.native="fetchMembers"
                @clear="fetchMembers"
              >
                <template #append>
                  <el-button icon="el-icon-search" @click="fetchMembers" />
                </template>
              </el-input>

              <div class="prd-spacer" />

              <el-button
                type="primary"
                icon="el-icon-check"
                size="medium"
                :disabled="selectedMembers.length === 0 || loadingMembers || loadingList || !renewalStatusLoaded || registerLoading"
                :loading="registerLoading"
                class="prd-btn-top"
                @click="openRegisterDialog"
              >
                Đăng ký gia hạn
                <span v-if="selectedMembers.length > 0">({{ selectedMembers.length }})</span>
              </el-button>
            </div>
          </div>

          <!-- Bảng danh sách thành viên gói (để chọn đăng ký) -->
          <div class="prd-table-wrapper" v-loading="loadingMembers">
            <el-table
              ref="membersTable"
              :data="memberList"
              border
              class="sk-table"
              :max-height="400"
              empty-text="Không có học sinh trong gói"
              @selection-change="handleMemberSelectionChange"
            >
              <el-table-column type="selection" :selectable="canRegisterMember" align="center" width="50" />
              <el-table-column type="index" label="STT" width="55" align="center" />
              <el-table-column prop="fullName" label="Họ và tên" min-width="180">
                <template slot-scope="{ row }">
                  <span class="prd-student-name">{{ row.fullName }}</span>
                </template>
              </el-table-column>
              <el-table-column label="Ngày sinh" width="120" align="center">
                <template slot-scope="{ row }">{{ row.birthDay | formatDate }}</template>
              </el-table-column>
              <el-table-column prop="className" label="Lớp học" min-width="130" />
              <el-table-column label="Trạng thái HS" width="130" align="center">
                <template slot-scope="{ row }">
                  <el-tag :type="kidStatusTagType(row.kidStatus)" size="small" effect="light">
                    {{ kidStatusLabel(row.kidStatus) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Trạng thái gia hạn" width="145" align="center">
                <template slot-scope="{ row }">
                  <el-tag
                    :type="getMemberRenewalStatusType(row.idStudent)"
                    size="mini"
                    effect="plain"
                  >
                    {{ getMemberRenewalStatusLabel(row.idStudent) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Tác vụ" width="110" align="center" fixed="right">
                <template slot-scope="{ row }">
                  <el-button
                    size="mini"
                    type="primary"
                    plain
                    icon="el-icon-plus"
                    :disabled="!canRegisterMember(row)"
                    @click="openSingleRegisterDialog(row)"
                  >
                    Đăng ký
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- Phân trang -->
          <div class="prd-pagination">
            <el-pagination
              background
              :current-page.sync="paginationMembers.page"
              :page-sizes="[25, 50, 100]"
              :page-size.sync="paginationMembers.size"
              :total="paginationMembers.total"
              layout="total, sizes, prev, pager, next"
              @size-change="(val) => { paginationMembers.size = val; paginationMembers.page = 1; fetchMembers(); }"
              @current-change="(val) => { paginationMembers.page = val; fetchMembers(); }"
            />
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="sk-dialog__footer">
        <el-button class="btn-cancel" plain @click="handleClose">Đóng</el-button>
      </div>
    </template>
  </dialog-base>

  <!-- Dialog hướng dẫn chi tiết -->
  <package-guide-dialog ref="guideDialog" />

  <!-- Dialog Chọn ngày đăng ký gia hạn -->
  <dialog-base
    :visible.sync="registerDialogVisible"
    title="Đăng ký tự động gia hạn"
    width="460px"
    :append-to-body="true"
    :use-panel="false"
    cancel-text="Hủy"
    confirm-text="Xác nhận"
    confirm-type="primary"
    confirm-icon="el-icon-check"
    :confirm-loading="registerLoading"
    :confirm-disabled="loadingMembers || loadingList || !renewalStatusLoaded"
    @cancel="registerDialogVisible = false"
    @confirm="submitRegister"
  >
    <div class="prd-register-dialog-body">
      <div class="prd-reg-summary-card mb-3">
        <div class="prd-reg-summary-item">
          <span class="prd-reg-label">Gói học phí:</span>
          <span class="prd-reg-value font-weight-bold text-primary">{{ packageName }}</span>
        </div>
        <div class="prd-reg-summary-item mt-2">
          <span class="prd-reg-label">Số học sinh đăng ký:</span>
          <el-tag size="small" type="primary" effect="dark">
            {{ registerTargetStudents.length }} học sinh
          </el-tag>
        </div>
      </div>

      <div class="prd-reg-form">
        <div class="prd-form-item">
          <label class="prd-form-label">
            <span class="text-danger font-weight-bold">*</span> Ngày đăng ký gia hạn:
          </label>
          <el-date-picker
            v-model="registerEnrollDate"
            type="date"
            placeholder="Chọn ngày đăng ký gia hạn"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            :clearable="false"
            style="width: 100%;"
          />
        </div>
        <div class="prd-reg-tip mt-3">
          <i class="el-icon-info text-primary" />
          <span>Ngày đăng ký gia hạn được gửi kèm về hệ thống để xác định thời điểm bắt đầu chu kỳ tính phí tự động.</span>
        </div>
      </div>
    </div>
  </dialog-base>
</div>
</template>

<script>
import DialogBase from "@/components/DialogBase.vue";
import PackageGuideDialog from "@/components/tutition_v3/dialog/PackageGuideDialog.vue";
import PackageRenewalService from "@/services/tutition_v3/PackageRenewalService";
import PackageMemberService from "@/services/tutition_v3/PackageMemberService";
import NotifyMessage from "@/commonFuncion/NotifyMessage";
import { kidStatusList } from "@/constant/listCommon";
import GradeService from "@/services/GradeService";
import MaClassService from "@/services/MaClassService";
import moment from "moment";

export default {
  name: "PackageRenewalDialog",
  components: { DialogBase, PackageGuideDialog },

  data() {
    return {
      visible: false,
      idPackage: null,
      packageName: "",
      packageAutoRenew: false,
      activeTab: "STATS",

      // --- Tab Stats ---
      loadingStats: false,
      stats: {},

      // --- Tab List ---
      loadingList: false,
      renewalList: [],
      selectedRenewals: [],
      bulkCancelLoading: false,
      bulkStatusLoading: false,
      // Map id -> trạng thái đang xử lý ('PAUSED'|'ACTIVE'|'CANCELLED'|null)
      statusLoadingMap: {},

      // --- Tab Register ---
      loadingMembers: false,
      memberList: [],
      selectedMembers: [],
      gradeList: [],
      classListRegister: [],
      kidStatusMap: {},
      searchRegister: { idGrade: "", idClass: "", fullName: "" },
      paginationMembers: { page: 1, size: 25, total: 0 },
      registerLoading: false,

      // Trạng thái renewal theo idStudent, dùng chung cho badge và điều kiện đăng ký.
      renewalStatusByStudent: {},
      renewalStatusLoaded: false,
      renewalListRequestId: 0,
      memberRequestId: 0,

      // Dialog đăng ký gia hạn
      registerDialogVisible: false,
      registerEnrollDate: "",
      registerTargetStudents: [],
    };
  },

  created() {
    kidStatusList.forEach(k => {
      this.kidStatusMap[k.key] = k.value;
    });
  },

  methods: {
    // ─── Public ──────────────────────────────────────────────────
    async open(pkg) {
      if (!pkg || !pkg.id) return;
      this.idPackage = pkg.id;
      this.packageName = pkg.name || "";
      this.packageAutoRenew = !!pkg.autoRenew;
      this.activeTab = "STATS";
      this._resetState();
      this.visible = true;

      if (this.gradeList.length === 0) {
        await this.loadGrades();
      }
      this.fetchStats();
    },

    handleClose() {
      this.visible = false;
      this.idPackage = null;
      this.renewalListRequestId++;
      this.memberRequestId++;
      this.renewalStatusLoaded = false;
      this.$emit("closed");
    },

    openGuideDialog() {
      this.$refs.guideDialog && this.$refs.guideDialog.open();
    },

    // ─── Tabs ────────────────────────────────────────────────────
    handleTabClick(tab) {
      if (!this.visible || !this.idPackage) return;
      if (tab.name === "STATS") this.fetchStats();
      if (tab.name === "LIST") this.fetchRenewalList();
      if (tab.name === "REGISTER") this.fetchMembers();
    },

    // ─── Tab Stats ───────────────────────────────────────────────
    async fetchStats() {
      if (!this.idPackage) return;
      this.loadingStats = true;
      try {
        const res = await PackageRenewalService.getStatistics({ idPackage: this.idPackage });
        this.stats = res.data.data || {};
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải thống kê renewal!"
        );
      } finally {
        this.loadingStats = false;
      }
    },

    // ─── Tab List ────────────────────────────────────────────────
    async fetchRenewalList() {
      if (!this.idPackage) return false;
      const idPackage = this.idPackage;
      const requestId = ++this.renewalListRequestId;
      this.loadingList = true;
      this.renewalStatusLoaded = false;
      this.clearMemberSelection();
      try {
        const res = await PackageRenewalService.getList({ idPackage });
        if (requestId !== this.renewalListRequestId || idPackage !== this.idPackage) return false;
        this.renewalList = res.data.data || [];
        const statuses = {};
        this.renewalList.forEach(row => {
          statuses[String(row.idStudent)] = row.status || "REGISTERED";
        });
        this.renewalStatusByStudent = statuses;
        this.renewalStatusLoaded = true;
        return true;
      } catch (err) {
        if (requestId !== this.renewalListRequestId || idPackage !== this.idPackage) return false;
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải danh sách đăng ký!"
        );
        return false;
      } finally {
        if (requestId === this.renewalListRequestId && idPackage === this.idPackage) this.loadingList = false;
      }
    },

    async handleUpdateStatus(row, newStatus) {
      // Xác nhận trước PAUSED / CANCELLED
      if (newStatus === "PAUSED" || newStatus === "CANCELLED") {
        const label = newStatus === "PAUSED" ? "tạm dừng" : "hủy";
        try {
          await this.$confirm(
            `Bạn có chắc chắn muốn ${label} gia hạn cho học sinh "${row.studentName}"?`,
            "Xác nhận thay đổi trạng thái",
            {
              confirmButtonText: "Xác nhận",
              cancelButtonText: "Hủy",
              type: "warning",
            }
          );
        } catch {
          return;
        }
      }

      // Disable nút trong khi request chạy
      this.$set(this.statusLoadingMap, row.id, newStatus);
      try {
        const payload = {
          idPackage: this.idPackage,
          idStudent: row.idStudent,
          status: newStatus,
          enabled: newStatus === "ACTIVE",
        };
        await PackageRenewalService.updateStatus(payload);
        NotifyMessage.leftCornerNotificationSuccess(
          `Cập nhật trạng thái "${this.getRenewalStatusLabel(newStatus)}" thành công!`
        );
        // Reload list + stats sau khi thành công
        await this.fetchRenewalList();
        this.fetchStats();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Cập nhật trạng thái thất bại!"
        );
      } finally {
        this.$set(this.statusLoadingMap, row.id, null);
      }
    },

    handleRenewalSelectionChange(val) {
      this.selectedRenewals = val;
    },

    selectedCountByStatus(status) {
      return this.selectedRenewals.filter(row => row.status === status).length;
    },

    async cancelSelectedRenewals() {
      if (!this.selectedRenewals.length) return;
      try {
        await this.$confirm(
          `Bạn có chắc chắn muốn hủy đăng ký gia hạn cho ${this.selectedRenewals.length} học sinh đã chọn?`,
          "Xác nhận hủy đăng ký",
          { confirmButtonText: "Xác nhận", cancelButtonText: "Hủy", type: "warning" }
        );
      } catch {
        return;
      }
      this.bulkCancelLoading = true;
      try {
        const res = await PackageRenewalService.cancelBulk({
          idPackage: this.idPackage,
          studentIds: this.selectedRenewals.map(row => row.idStudent),
        });
        const count = res.data.data?.cancelledCount ?? this.selectedRenewals.length;
        NotifyMessage.leftCornerNotificationSuccess(`Đã hủy đăng ký ${count} học sinh!`);
        this.selectedRenewals = [];
        await this.fetchRenewalList();
        this.fetchStats();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Hủy đăng ký gia hạn thất bại!"
        );
      } finally {
        this.bulkCancelLoading = false;
      }
    },

    async updateSelectedRenewals(status) {
      const targetRows = this.selectedRenewals.filter(row =>
        status === "PAUSED" ? row.status === "ACTIVE" : row.status === "PAUSED"
      );
      if (!targetRows.length) return;
      const label = status === "PAUSED" ? "tạm dừng" : "kích hoạt lại";
      try {
        await this.$confirm(
          `Bạn có chắc chắn muốn ${label} gia hạn cho ${targetRows.length} học sinh đã chọn?`,
          "Xác nhận cập nhật trạng thái",
          { confirmButtonText: "Xác nhận", cancelButtonText: "Hủy", type: "warning" }
        );
      } catch {
        return;
      }
      this.bulkStatusLoading = true;
      try {
        const res = await PackageRenewalService.updateStatusBulk({
          idPackage: this.idPackage,
          studentIds: targetRows.map(row => row.idStudent),
          status,
          enabled: status === "ACTIVE",
        });
        const count = res.data.data?.updatedCount ?? targetRows.length;
        NotifyMessage.leftCornerNotificationSuccess(`Đã ${label} ${count} học sinh!`);
        this.selectedRenewals = [];
        await this.fetchRenewalList();
        this.fetchStats();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Cập nhật trạng thái thất bại!"
        );
      } finally {
        this.bulkStatusLoading = false;
      }
    },

    // ─── Tab Register ─────────────────────────────────────────────
    async loadGrades() {
      try {
        const res = await GradeService.getGradeInPrinciple();
        this.gradeList = res.data.data || [];
      } catch (err) {
        console.error("Lỗi tải khối:", err);
      }
    },

    async handleRegisterGradeChange() {
      const gradeId = this.searchRegister.idGrade;
      if (!gradeId) {
        this.classListRegister = [];
        this.searchRegister.idClass = "";
        this.fetchMembers();
        return;
      }
      try {
        const res = await MaClassService.getClassInGrade(gradeId);
        this.classListRegister = res.data?.data || [];
        if (this.classListRegister.length > 0) {
          this.searchRegister.idClass = this.classListRegister[0].id;
        } else {
          this.searchRegister.idClass = "";
          this.memberList = [];
          this.paginationMembers.total = 0;
          return;
        }
      } catch (err) {
        console.error(err);
      }
      this.fetchMembers();
    },

    async fetchMembers() {
      if (!this.idPackage) return;
      const idPackage = this.idPackage;
      const requestId = ++this.memberRequestId;
      this.loadingMembers = true;
      this.memberList = [];
      this.paginationMembers.total = 0;
      this.clearMemberSelection();
      try {
        const params = {
          idPackage,
          idClass: this.searchRegister.idClass || undefined,
          fullName: this.searchRegister.fullName || undefined,
          page: this.paginationMembers.page - 1,
          size: this.paginationMembers.size,
        };
        // Luôn tải lại trạng thái, kể cả khi tab Danh sách đã được mở trước đó.
        const [res] = await Promise.all([
          PackageMemberService.getMembers(params),
          this.fetchRenewalList(),
        ]);
        if (requestId !== this.memberRequestId || idPackage !== this.idPackage) return;
        this.memberList = res.data.data || [];
        this.paginationMembers.total = res.data.total || 0;
      } catch (err) {
        if (requestId !== this.memberRequestId || idPackage !== this.idPackage) return;
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Lỗi khi tải danh sách thành viên!"
        );
      } finally {
        if (requestId === this.memberRequestId && idPackage === this.idPackage) this.loadingMembers = false;
      }
    },

    clearMemberSelection() {
      this.selectedMembers = [];
      this.$refs.membersTable?.clearSelection();
    },

    handleMemberSelectionChange(val) {
      this.selectedMembers = val.filter(row => this.canRegisterMember(row));
    },

    openRegisterDialog() {
      const students = this.selectedMembers.filter(row => this.canRegisterMember(row));
      if (!students.length) {
        NotifyMessage.leftCornerNotificationWarning(
          "Vui lòng chọn ít nhất một học sinh để đăng ký gia hạn!"
        );
        return;
      }
      this.registerTargetStudents = students;
      this.registerEnrollDate = moment().format("YYYY-MM-DD");
      this.registerDialogVisible = true;
    },

    openSingleRegisterDialog(row) {
      if (!this.canRegisterMember(row)) return;
      this.registerTargetStudents = [row];
      this.registerEnrollDate = moment().format("YYYY-MM-DD");
      this.registerDialogVisible = true;
    },

    async submitRegister() {
      if (this.registerLoading) return;
      if (!this.registerEnrollDate) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng chọn ngày đăng ký gia hạn!");
        return;
      }
      if (!this.registerTargetStudents.length) {
        this.registerDialogVisible = false;
        return;
      }
      if (this.registerTargetStudents.some(row => !this.canRegisterMember(row))) {
        NotifyMessage.leftCornerNotificationWarning("Chỉ có thể đăng ký gia hạn cho học sinh chưa đăng ký. Vui lòng kiểm tra lại danh sách!");
        return;
      }

      const studentIds = this.registerTargetStudents.map((s) => s.idStudent);
      const payload = {
        idPackage: this.idPackage,
        studentIds,
        enrollDate: this.registerEnrollDate,
      };

      this.registerLoading = true;
      try {
        const res = await PackageRenewalService.register(payload);
        const count = res.data.data?.registeredCount ?? studentIds.length;
        NotifyMessage.leftCornerNotificationSuccess(
          `Đăng ký thành công ${count} học sinh vào gia hạn!`
        );
        this.registerDialogVisible = false;
        this.selectedMembers = [];
        this.registerTargetStudents = [];
        // Reload list + stats + members; không tự chèn bản ghi từ request
        this.fetchStats();
        await this.fetchMembers();
      } catch (err) {
        // Giữ lựa chọn; hiển thị message từ BE; không hiển thị thành công một phần
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Đăng ký gia hạn thất bại!"
        );
      } finally {
        this.registerLoading = false;
      }
    },

    // ─── Helpers ─────────────────────────────────────────────────
    isRegistered(idStudent) {
      return Object.prototype.hasOwnProperty.call(this.renewalStatusByStudent, String(idStudent));
    },

    canRegisterMember(row) {
      return !!row && row.idStudent != null && this.renewalStatusLoaded
        && !this.loadingMembers && !this.loadingList && !this.registerLoading
        && !this.isRegistered(row.idStudent);
    },

    getMemberRenewalStatusLabel(idStudent) {
      if (!this.renewalStatusLoaded) return this.loadingList ? "Đang tải..." : "Chưa xác định";
      if (!this.isRegistered(idStudent)) return "Chưa đăng ký";
      const status = this.renewalStatusByStudent[String(idStudent)];
      return status === "ACTIVE" || status === "REGISTERED" ? "Đã đăng ký" : this.getRenewalStatusLabel(status);
    },

    getMemberRenewalStatusType(idStudent) {
      if (!this.renewalStatusLoaded) return "info";
      return this.getRenewalStatusType(this.renewalStatusByStudent[String(idStudent)]);
    },

    getRenewalStatusLabel(status) {
      const map = {
        ACTIVE: "Đang hoạt động",
        PAUSED: "Tạm dừng",
        CANCELLED: "Đã hủy",
        EXPIRED: "Đã hết hạn",
      };
      return map[status] || status || "—";
    },

    getRenewalStatusType(status) {
      const map = {
        ACTIVE: "success",
        PAUSED: "warning",
        CANCELLED: "danger",
        EXPIRED: "info",
      };
      return map[status] || "info";
    },

    formatRenewalCycle(cycle, duration) {
      const map = { DAILY: "Ngày", MONTHLY: "Tháng", YEARLY: "Năm" };
      const label = map[cycle];
      if (!label) return "—";
      return duration && duration > 1 ? `${duration} ${label}` : label;
    },

    /** Kiểm tra sắp hết hạn (trong 7 ngày) */
    isExpiringSoon(dateStr) {
      if (!dateStr) return false;
      const diffDays = this.$moment
        ? this.$moment(dateStr).diff(this.$moment(), "days")
        : Math.ceil((new Date(dateStr) - new Date()) / 86400000);
      return diffDays >= 0 && diffDays <= 7;
    },

    formatDate(value) {
      if (!value) return "—";
      return moment(value).format("DD/MM/YYYY");
    },

    kidStatusLabel(status) {
      return this.kidStatusMap[status] || status;
    },

    kidStatusTagType(status) {
      if (status === "STUDYING") return "success";
      if (status === "STUDY_WAIT") return "warning";
      if (status === "RESERVE") return "info";
      if (status === "LEAVE_SCHOOL") return "danger";
      return "";
    },

    _resetState() {
      this.stats = {};
      this.renewalList = [];
      this.selectedRenewals = [];
      this.bulkCancelLoading = false;
      this.bulkStatusLoading = false;
      this.statusLoadingMap = {};
      this.memberList = [];
      this.selectedMembers = [];
      this.classListRegister = [];
      this.searchRegister = { idGrade: "", idClass: "", fullName: "" };
      this.paginationMembers = { page: 1, size: 25, total: 0 };
      this.renewalStatusByStudent = {};
      this.renewalStatusLoaded = false;
      this.renewalListRequestId++;
      this.memberRequestId++;
      this.loadingList = false;
      this.loadingMembers = false;
      this.registerDialogVisible = false;
      this.registerEnrollDate = "";
      this.registerTargetStudents = [];
    },
  },

  filters: {
    formatDate(value) {
      if (!value) return "—";
      return moment(value).format("DD/MM/YYYY");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/style/sk-dialog";
@import "@/assets/style/sk-table";

/* =====================================================
   PackageRenewalDialog – Prefix: prd-
   ===================================================== */

.prd-content { margin-top: -6px; }

.prd-panel-text {
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;

  .sk-panel-title,
  .sk-panel-subtitle {
    word-break: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }
}

/* Meta badges (gia hạn on/off) */
.prd-meta-badges {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
  justify-content: flex-end;
}

/* ─── Tabs ─────────────────────────────────────────── */
/deep/ .prd-tabs .el-tabs__nav {
  background: transparent;
  border: none;
}
/deep/ .prd-tabs .el-tabs__nav-wrap::after {
  background: #2563eb;
  height: 2px;
}
/deep/ .prd-tabs .el-tabs__item {
  background: #e5e7eb;
  color: #4b5563;
  font-weight: 600;
  border: 1px solid #d1d5db !important;
  border-bottom: none !important;
  border-radius: 8px 8px 0 0;
  transition: background 0.15s, color 0.15s;
  i { margin-right: 6px; }
}
/deep/ .prd-tabs .el-tabs__item:hover:not(.is-active) {
  background: #d1d5db;
  color: #1e293b;
}
/deep/ .prd-tabs .el-tabs__item.is-active {
  color: #ffffff !important;
  background: #2563eb !important;
  border-color: #2563eb !important;
}
/deep/ .prd-tabs .el-tabs__header { margin-bottom: 14px; }

/* ─── Stats Grid ───────────────────────────────────── */
.prd-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.prd-stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
  transition: box-shadow 0.15s;

  &:hover { box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08); }

  &--active  { border-color: #bbf7d0; background: #f0fdf4; }
  &--paused  { border-color: #fde68a; background: #fffbeb; }
  &--cancelled { border-color: #fecaca; background: #fff5f5; }
  &--due     { border-color: #c7d2fe; background: #eef2ff; }
  &--cycle   { border-color: #bfdbfe; background: #eff6ff; }
  &--invoiced { border-color: #bbf7d0; background: #f0fdf4; }
  &--pending  { border-color: #e9d5ff; background: #faf5ff; }
}

.prd-stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;

  .prd-stat-card--active    & { background: #dcfce7; color: #16a34a; }
  .prd-stat-card--paused    & { background: #fef9c3; color: #ca8a04; }
  .prd-stat-card--cancelled & { background: #fee2e2; color: #dc2626; }
  .prd-stat-card--due       & { background: #e0e7ff; color: #4f46e5; }
  .prd-stat-card--cycle     & { background: #dbeafe; color: #2563eb; }
  .prd-stat-card--invoiced  & { background: #dcfce7; color: #16a34a; }
  .prd-stat-card--pending   & { background: #f3e8ff; color: #9333ea; }
}

.prd-stat-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
  word-break: break-word;
  overflow-wrap: break-word;
}

.prd-stat-value {
  font-size: 22px;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.1;
  word-break: break-word;
}

.prd-stat-label {
  font-size: 11.5px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.3;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

/* ─── Latest Cycle ─────────────────────────────────── */
.prd-latest-cycle {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 14px;
  word-break: break-word;
  overflow-wrap: break-word;
}

.prd-latest-cycle-head {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #1e40af;
  flex-wrap: wrap;
  word-break: break-word;
}

.prd-latest-cycle-title {
  font-weight: 700;
  word-break: break-word;
  white-space: normal;
}

.prd-latest-cycle-dates {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  flex-wrap: wrap;
  word-break: break-word;
}

.prd-date-label { color: #64748b; font-weight: 500; word-break: break-word; white-space: normal; }
.prd-date-value { font-weight: 700; color: #1e293b; word-break: break-word; white-space: normal; }
.prd-date-arrow { color: #94a3b8; font-size: 12px; flex-shrink: 0; }

.prd-empty-cycle {
  color: #64748b;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 0;
  margin-bottom: 14px;
  word-break: break-word;
  white-space: normal;
}

.prd-stats-action, .prd-list-action {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

/* ─── Note box ─────────────────────────────────────── */
.prd-note-box {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  color: #78350f;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;

  i { font-size: 15px; color: #d97706; flex-shrink: 0; margin-top: 2px; }
  strong, span, div { word-break: break-word; overflow-wrap: break-word; }
}

/* ─── Register date row ────────────────────────────── */
.prd-register-date-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.prd-register-date-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}
.prd-register-date-sep {
  color: #94a3b8;
  font-size: 16px;
}

/* ─── Filter Card ──────────────────────────────────── */
.prd-filter-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
}

.prd-filter-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.prd-filter-control--grade { width: 140px; }
.prd-filter-control--class { width: 150px; }
.prd-search-input          { width: 260px; }
.prd-spacer                { flex: 1; }
.prd-btn-top               { border-radius: 8px; font-weight: 600; }

/* ─── Table ────────────────────────────────────────── */
.prd-table-wrapper {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
}

.prd-student-name {
  font-weight: 600;
  color: #1e293b;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}
.prd-text-muted   { color: #94a3b8; font-size: 13px; word-break: break-word; white-space: normal; }
.prd-text-danger  { color: #dc2626; font-weight: 600; word-break: break-word; white-space: normal; }
.prd-text-primary { color: #2563eb; font-weight: 600; word-break: break-word; white-space: normal; }

/deep/ .sk-table thead th,
/deep/ .sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
/deep/ .sk-table thead th > .cell,
/deep/ .sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
  line-height: 1.3 !important;
}
/deep/ .sk-table thead tr { height: 46px; }
/deep/ .sk-table tbody td { background: #ffffff; }
/deep/ .sk-table tbody td > .cell {
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: break-word !important;
  line-height: 1.4 !important;
}
/deep/ .sk-table .el-tag {
  white-space: normal !important;
  height: auto !important;
  line-height: 1.3 !important;
  padding: 4px 8px !important;
  word-break: break-word !important;
  display: inline-block;
}

/* ─── Pagination ───────────────────────────────────── */
.prd-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}

/* ─── Controls bo góc ──────────────────────────────── */
/deep/ .prd-content .el-input__inner,
/deep/ .prd-content .el-select .el-input__inner { border-radius: 8px; }
/deep/ .prd-content .el-button { border-radius: 8px; }

/* ─── Register Confirm Dialog ──────────────────────── */
.prd-register-dialog-body {
  padding: 4px 0;
}

.prd-reg-summary-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 16px;
  word-break: break-word;
  overflow-wrap: break-word;
}

.prd-reg-summary-item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.prd-reg-label {
  font-size: 13.5px;
  color: #64748b;
  font-weight: 500;
  min-width: 140px;
  flex-shrink: 0;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}

.prd-reg-value {
  font-size: 14px;
  color: #1e293b;
  flex: 1;
  min-width: 0;
  text-align: right;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
}

.prd-form-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.prd-form-label {
  font-size: 13.5px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
}

.prd-reg-tip {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.5;
  margin-bottom: 0;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;

  i {
    font-size: 15px;
    margin-top: 2px;
    flex-shrink: 0;
  }
  span {
    word-break: break-word;
    overflow-wrap: break-word;
    white-space: normal;
  }
}
</style>
